import './HexagonRadarChart.css';

import { brandColors } from '@src/assets/colors/brandColors';
import { HexagonDataItem } from '@src/features/company/dx-diagnosis/DxDiagnosisResultForm';
import { useState } from 'react';
import {
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';

interface PayloadData {
  payload: HexagonDataItem;
  dataKey: string;
}

// ホバー時に表示するカスタムツールチップコンポーネント
const CustomTooltip: React.FC<{ active?: boolean; payload?: HexagonDataItem }> = ({
  active,
  payload,
}) => {
  if (active && payload) {
    const { subject, value } = payload;
    return (
      <div className='rounded-md border border-gray-200 bg-white  p-3'>
        <p className='text-[10px]'>{subject}</p>
        <p>
          <span className='text-base'>
            <span className='text-primary'>{value}</span>点
          </span>
          <span className='text-sm'>／20点</span>
        </p>
      </div>
    );
  }
  return null;
};

interface CustomDotProps {
  cx?: number;
  cy?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload?: any;
  dataKey?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onMouseEnter?: (payload: any, dataKey: string) => void;
  onMouseLeave?: () => void;
}

const CustomDot: React.FC<CustomDotProps> = (props) => {
  const { cx, cy, payload, dataKey, onMouseEnter, onMouseLeave } = props;
  const [isHovered, setIsHovered] = useState<boolean>(false);

  return (
    <circle
      cx={cx}
      cy={cy}
      r={isHovered ? 5 : 4}
      fill={isHovered ? brandColors['500'] : brandColors['400']}
      stroke={isHovered ? brandColors['500'] : brandColors['400']}
      fillOpacity='1'
      onMouseEnter={() => {
        setIsHovered(true);
        if (onMouseEnter && dataKey) {
          onMouseEnter(payload.payload, dataKey);
        }
      }}
      onMouseLeave={() => {
        setIsHovered(false);
        if (onMouseLeave) {
          onMouseLeave();
        }
      }}
    />
  );
};

const HexagonRadarChart = ({ data }: { data: HexagonDataItem[] }) => {
  const [activeTooltip, setActiveTooltip] = useState<PayloadData | null>(null);

  const handleDotMouseEnter = (payload: HexagonDataItem, dataKey: string): void => {
    setActiveTooltip({ payload, dataKey } as PayloadData);
  };

  const handleDotMouseLeave = () => {
    setActiveTooltip(null);
  };
  return (
    <>
      <ResponsiveContainer
        width='100%'
        height='100%'
        minHeight={400}
      >
        <RadarChart
          width={532}
          height={448}
          data={data}
          style={{ margin: '0 auto' }}
        >
          <PolarGrid
            gridType='polygon'
            stroke='#E4E4E4'
            radialLines={false}
          />
          <PolarAngleAxis
            dataKey='subject'
            tick={(props) => {
              const { x, y, payload } = props;
              // ラベルの位置を調整
              const adjustedX = () => {
                if (payload.index === 1) {
                  return x + 20; // 右上
                }
                if (payload.index === 2) {
                  return x + 20; // 右下
                }
                if (payload.index === 4) {
                  return x - 20; // 左下
                }
                if (payload.index === 5) {
                  return x - 20; // 左上
                }
                return x;
              };
              const adjustedY = () => {
                if (payload.index === 1) {
                  return y - 10; // 右上
                }
                if (payload.index === 2) {
                  return y + 20; // 右下
                }
                if (payload.index === 3) {
                  return y + 20; // 下
                }
                if (payload.index === 4) {
                  return y + 20; // 左下
                }
                if (payload.index === 5) {
                  return y - 10; // 左上
                }
                return y;
              };

              return (
                <text
                  x={adjustedX()}
                  y={adjustedY()}
                  textAnchor='middle'
                  fill='#333333'
                  className='text-[clamp(8px,calc(100vw/1080*11),12px)] text-gray-500'
                >
                  {payload.value}
                </text>
              );
            }}
          />
          <PolarRadiusAxis
            angle={90}
            domain={[0, 20]}
            ticks={[0, 5, 10, 15, 20]}
            tick={{
              className: 'text-[#888] text-xs text-',
              dy: 7,
            }}
            style={{
              textShadow:
                '0 0px 1px #fff, 0 0px 1px #fff, 0 0px 1px #fff, 0 0px 1px #fff, 0 0px 1px #fff',
            }}
            orientation='middle'
            axisLine={false}
          />
          <Radar
            name='データ'
            dataKey='value'
            stroke={brandColors['400']}
            fill='rgba(255,255,255,0)'
            fillOpacity={0.3}
            dot={(props) => {
              const { key, ...restProps } = props;
              return (
                <CustomDot
                  key={key}
                  {...restProps}
                  onMouseEnter={handleDotMouseEnter}
                  onMouseLeave={handleDotMouseLeave}
                />
              );
            }}
            activeDot={false}
          />
          <Tooltip
            active={activeTooltip !== null}
            content={({ active }) => {
              if (!activeTooltip) {
                return null;
              }

              return (
                <CustomTooltip
                  active={active}
                  payload={activeTooltip.payload}
                />
              );
            }}
          />
        </RadarChart>
      </ResponsiveContainer>
      <style>
        {`
          .recharts-polar-grid-concentric .recharts-polar-grid-concentric-polygon:nth-child(3) {
            stroke-width: 4;
            stroke: #BBBBBB;
          }
        `}
      </style>
    </>
  );
};

export default HexagonRadarChart;
